// ============================================================================

import { firstLesion, interventionAnnotations } from '@/utils/clinicalAnnotations'
// 口述內容 → 截圖卡片的四個欄位
//
// 醫師講「photo 3」把第 3 張截圖切成編輯狀態，接著口述的內容寫進那張卡片：
//
//     photo 3 … ascending colon … polyp … 5 to 10 mm … 結束編輯
//                    ↓                ↓          ↓
//                 landmarkID   evas2_lesion_name  evas2_size_text
//
// ── 資料從哪來 ────────────────────────────────────────────────────────────
//
// 不是自己解析逐字稿。每一句 refine 都自帶 `keywords`，那是 KKASR 的
// key_info_extractor 即時算出來的正規化字串（Key Info 面板顯示的就是它）。
// 用同一份資料的好處是**醫師看到什麼就存到什麼**——畫面上 Key Info 出現
// 「polyp」，卡片就會是 polyp，不會有兩套解析結果打架。
//
// ── 三個欄位三種判斷方式，刻意不一樣 ──────────────────────────────────────
//
//   size      regex。尺寸是結構化的（數字＋單位），KKASR 已經正規化成
//             `size 5~10 mm` / `distance 10~20 cm` 這種固定格式。
//   location  比對 **DB 的 landmarks 主檔**。因為 DB 欄位存的是 landmarkID，
//             不是字串——非查主檔不可。KKASR 的 location 詞表是它的子集。
//   lesion    比對 **KKASR 的 lesion 詞表**（/key_info_terms）。不能用「剩下的
//             都算病灶名」——那會把 sessile（型態）、biopsy（處置）、good
//             （清腸程度）通通當成病灶名寫進卡片。
//
// ── last-wins ────────────────────────────────────────────────────────────
//
// 醫師會改口（「polyp…不對，是 adenoma」）。同一個編輯區間裡後講的蓋前面的，
// 與 Cleansing Level／圖鑑同一個語義。也因此重放整段指令與只看最後一次結果
// 相同——F5 冪等。
// ============================================================================

/**
 * KKASR 對尺寸／距離的正規化輸出格式（見 key_info_extractor._iter_measurements）：
 *   `size 5~10 mm` / `size 8 mm` / `distance 10~20 cm` / `distance 15 cm`
 *
 * 卡片上不寫 "size" 那個字（兩列都很擠，而「數字＋mm」不會被誤讀），所以這裡
 * 把前綴拿掉。distance 的前綴**要留著**——`10~20 cm` 不寫 distance 會被當成
 * 病灶大小，那是臨床上完全不同的意思。
 */
const _SIZE_RE = /^size\s+(.+)$/i
const _DISTANCE_RE = /^distance\s+(.+)$/i

/**
 * 口述路徑**不採用**的位置詞（正規化後的小寫形式）。
 *
 * `cecum` 同時是三種東西：配速員的里程碑指令、KKASR 的 key_info location 詞、
 * landmarks 主檔的一列。三個決定各自都對，疊起來卻是靜默的資料污染——醫師在
 * 編輯 P3 的途中回頭補講「cecum」（里程碑）或退鏡時重看回盲部，卡片上已經口述
 * 好的 `sigmoid colon` 會被 last-wins 蓋成 `cecum`，畫面上沒有任何提示。
 *
 * 取捨（規格書 P0-5）：把「語音把病灶標在盲腸」這條捷徑讓掉，換掉「無提示的
 * 誤寫」。真的要標在盲腸時走卡片既有的非語音途徑。配速員那條路徑不受影響——
 * 里程碑事件在 store 的 consumeCommands 處理，與這裡是兩條路。
 *
 * ⚠️ 只排除 cecum（含英式拼法 caecum）。`splenic flexure` 雖然 KKASR 仍會送
 *    事件，但 EVAS2.0 已不記那一步、而且它寫成位置在語義上是對的，**不排除**。
 */
const DICTATION_EXCLUDED_LANDMARKS = new Set(['cecum', 'caecum'])

// Key Info 為了忠實顯示會保留中文原詞，但 DB landmarks 是英文主檔。
// alias 只做儲存時的對照，不改面板內容；目前凍結需求只新增直腸這一個必要映射。
const DICTATION_LANDMARK_ALIASES = new Map([
  ['直腸', 'rectum'],
])

/**
 * 一段截圖編輯區間最長活多久（秒）。
 *
 * 醫師常常忘了講「結束編輯」（Exam2DetailPage 的註解自承這件事），而區間沒有
 * 收尾就是開到天荒地老——之後每一句的病灶名／大小／位置都會 last-wins 蓋進
 * 同一張卡片，最終停在全場最後提到的病灶。上限到期即視為關閉。
 *
 * 90 秒的理由：口述一張截圖的內容實測是數秒到十數秒，90 秒已經是「醫師顯然
 * 早就講完了」的量級；再長就不是口述這張圖，是在講下一段檢查。
 *
 * ⚠️ 判定方式刻意是「重算時比對時間戳」而不是計時器：photoEditWindows 是純
 *    函式重算（F5 之後指令從 cursor 0 重放），塞計時器進來就不冪等了。
 */
export const PHOTO_EDIT_MAX_OPEN_SEC = 90

/**
 * 把 keyword 陣列歸類成卡片的四個欄位。
 *
 * @param {string[]} keywords            這一段時間內所有 refine 句子的 keywords（依時間串起來）
 * @param {Array<{id:number, landmark:string}>} landmarkList  DB 的 landmarks 主檔
 * @param {Object|string[]} termCategories KKASR /key_info_terms 的完整分類；舊陣列視為 lesion
 * @returns {{lesionName: string|null, distanceText: string|null, sizeText: string|null, landmarkID: number|null}}
 *          null = 這一段沒有講到那個欄位（呼叫端不要覆寫既有的值）
 */
export function dictationFields(keywords, landmarkList, termCategories) {
  const words = Array.isArray(keywords) ? keywords : []
  const landmarks = Array.isArray(landmarkList) ? landmarkList : []
  const categories = Array.isArray(termCategories)
    ? { lesion: termCategories, intervention: [] }
    : (termCategories || {})
  const lesions = Array.isArray(categories.lesion) ? categories.lesion : []
  const interventions = Array.isArray(categories.intervention) ? categories.intervention : []

  // 主檔名稱可能有底線寫法（appendix_orifice），與 keyword 的空白寫法對不起來
  const norm = (s) => String(s || '').toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim()

  const landmarkByName = new Map()
  for (const item of landmarks) {
    const key = norm(item && item.landmark)
    // 主檔同時有 `appendix orifice` 與 `appendix_orifice` 兩列，正規化後同名。
    // 保留先出現的——兩者指向同一個解剖位置，選哪個都對，但要穩定。
    if (key && !landmarkByName.has(key)) landmarkByName.set(key, Number(item.id))
  }

  const lesionSet = new Set(lesions.map(norm).filter(Boolean))
  const interventionSet = new Set(interventions.map(norm).filter(Boolean))
  let clinicalAnnotations = []

  let lesionName = null
  let distanceText = null
  let sizeText = null
  let landmarkID = null

  for (const raw of words) {
    const word = String(raw || '').trim()
    if (!word) continue

    const size = word.match(_SIZE_RE)
    if (size) {
      sizeText = size[1].trim()
      continue
    }
    const distance = word.match(_DISTANCE_RE)
    if (distance) {
      distanceText = distance[1].trim()
      continue
    }

    const sourceKey = norm(word)
    const key = DICTATION_LANDMARK_ALIASES.get(sourceKey) || sourceKey
    // 配速員里程碑詞在口述路徑一律不當位置用（見 DICTATION_EXCLUDED_LANDMARKS）。
    // 擺在位置比對**之前**：它就在 landmarks 主檔裡，比對得到才需要擋。
    //
    // ⚠️ 這個 continue 也擋掉了下面的病灶名比對。目前 KKASR 的 lesion 詞表沒有
    //    cecum（它在 location 類別），所以沒有差別；哪天 lesion 詞表出現同名詞，
    //    這裡會連病灶名一起吞掉——那時要把排除範圍收窄成「只跳過位置比對」。
    if (DICTATION_EXCLUDED_LANDMARKS.has(key)) continue
    if (landmarkByName.has(key)) {
      landmarkID = landmarkByName.get(key)
      continue
    }
    if (lesionSet.has(key)) {
      // 顯示形式用醫師講的那個寫法（KKASR 已正規化過大小寫），不強制轉成詞表
      // 裡的形式——兩者只差大小寫時，畫面上與 Key Info 一致比較不會讓人困惑。
      lesionName = key === '息肉' ? 'Polyp' : word
      clinicalAnnotations = clinicalAnnotations.filter((entry) => entry.category !== 'lesion')
      clinicalAnnotations.push({ category: 'lesion', term: lesionName })
      continue
    }
    if (interventionSet.has(key)) {
      // 處置可與病灶共存；同一句重複詞只留第一次，但不能覆寫已口述的病灶。
      if (!clinicalAnnotations.some((entry) => entry.category === 'intervention' && norm(entry.term) === key)) {
        clinicalAnnotations.push({ category: 'intervention', term: word })
      }
    }
  }

  const spokenLesion = firstLesion(clinicalAnnotations)
  if (spokenLesion) lesionName = spokenLesion
  const fields = {
    lesionName,
    distanceText,
    sizeText,
    landmarkID,
  }
  const typedInterventions = interventionAnnotations(clinicalAnnotations)
  if (typedInterventions.length) fields.clinicalAnnotations = clinicalAnnotations
  return fields
}

/**
 * 把指令事件攤成「編輯區間」。
 *
 * `photo_select` 或 `image_annotate` 開一段，遇到下一個開啟指令或 `photo_over`
 * 收尾；都沒有就是還開著（endSec = null）。兩個開啟指令的差別只在醫師說法：
 * 舊版是 `photo N`，EVAS2 影像生命週期是「標記圖 N」；後面的欄位解析與 DB
 * 寫入必須走同一條管線，不能各維護一份。
 *
 * ⚠️ 從**完整的指令清單**重算，不是逐筆累加。F5 之後指令會從 cursor 0 重放，
 *    重算才保證得到與重整前相同的結果（冪等）。
 *
 * ⚠️ `endSource` 記的是「誰收的尾」，不是裝飾用的：語音 photo_over 的秒數來自
 *    fast 通道（即時、不落後），滑鼠按鈕的秒數來自**已 commit 的 refine**（天生
 *    落後好幾秒），兩者的有效邊界算法不一樣（見 photoEditWindowRange）。
 *
 * @param {Array<{command:string, at_sec:number, param:number|null, source?:string}>} commands
 * @returns {Array<{param:number|null, startSec:number, endSec:number|null, endSource:string|null}>}
 */
export function photoEditWindows(commands) {
  const opensWindow = (event) => (
    event.command === 'photo_select' || event.command === 'image_annotate'
  )
  const events = (Array.isArray(commands) ? commands : [])
    .filter((e) => e && (opensWindow(e) || e.command === 'photo_over'))
    .slice()
    .sort((a, b) => Number(a.at_sec || 0) - Number(b.at_sec || 0))

  const windows = []
  for (const event of events) {
    const atSec = Number(event.at_sec || 0)
    const open = windows.length ? windows[windows.length - 1] : null
    if (open && open.endSec === null) {
      open.endSec = atSec
      open.endSource = event.source === 'manual' ? 'manual' : 'command'
    }
    if (opensWindow(event)) {
      const param = Number(event.param)
      windows.push({
        param: Number.isInteger(param) && param > 0 ? param : null,
        startSec: atSec,
        endSec: null,
        endSource: null,
      })
    }
  }
  return windows
}

/**
 * 滑鼠收尾要往後放行多少秒（＝ refine 的已知落後量）。
 *
 * ⚠️ 這個數字是**對齊後端**來的：`KKASR/KKApp/config.ini` 的 `[extend_window]`
 *    `max_window_sec = 12.0`——那是「還沒 commit 的音訊視窗」上限，也就是
 *    「醫師已經講了、但逐字稿還沒吐出來」的最大量。
 *    （同一區的 `stable_repeat_count = 3` × `normal_append_sec = 2.0` 表示首次
 *    commit 至少要 6 秒新音訊，所以落後好幾秒是常態不是例外。）
 *
 * ── 為什麼需要它 ──
 *
 * 滑鼠收尾的秒數只能取「已 commit 的最後位置」（getAsrNowSec）——那是音訊時間軸
 * 上唯一拿得到、而且 F5 重放後還算得出同一個值的座標。但它**落後於醫師按鈕的
 * 真實時刻**：最自然的用法「口述完，按鈕收工」正好是它落後最多的一刻。不補這段
 * 落後的話，區間會塌成 `to = from`，醫師剛講完的整段**靜默消失**。
 *
 * ⚠️ **放行量一定要再夾一次「下一個區間的起點」**（見 photoEditWindowRanges）。
 *    滑鼠標記落後真實時刻 `lag` 秒，但下一個 `photo_select` 走 fast 通道**不落後**，
 *    所以 `下一個區間的起點 < 標記 + 12` 是常態 ⇒ 兩個區間的有效範圍重疊
 *    `12 - lag` 秒；一行完全落在重疊區時與兩者重疊秒數**完全相等** ⇒ 命中平手
 *    規則 ⇒ 判給索引較小的**舊**卡片。實測誤寫率 5.53%，而且方向反直覺——
 *    refine 越跟得上（lag 越小）放行量越多餘、重疊越大、誤寫機率越高。
 *    後來的 `photo_select` 是比「落後量啟發式」強得多的證據，一律以它為準。
 *
 * ⚠️ 夾完之後剩下的代價：按鈕之後才講、**而且醫師沒有接著拍下一張**時，落在這
 *    12 秒內的內容仍會寫進這張卡片。相對於「剛講完的整段消失」，方向是刻意選的。
 *    KKASR 若比 max_window_sec 落後更多（大量積壓），超出的那段仍會漏——已知殘留。
 *
 * ⚠️ 後端那個值改了，這裡要跟著改。
 */
export const REFINE_COMMIT_LAG_SEC = 12

/**
 * 一段編輯區間的**有效**時間範圍 `[from, to)`。
 *
 * 與 window.endSec 的差別有兩個：
 *
 *   1. 套上 PHOTO_EDIT_MAX_OPEN_SEC 的自動關閉——沒有收尾指令的區間不再開到
 *      Infinity，忘了講「結束編輯」的那一段也不會把之後整場的口述吃進來。
 *   2. **滑鼠收尾**額外放行 REFINE_COMMIT_LAG_SEC，補回 refine 的已知落後量。
 *      語音 photo_over 不放行——它的秒數來自 fast 通道，本來就對得準。
 *
 * ⚠️ 自動關閉對**每一段**區間都成立，不只最後那段還開著的。醫師忘了講結束詞
 *    時，收尾的往往是十分鐘後的下一個 `photo N`——那一段同樣會把中間所有口述
 *    灌進同一張卡片，與「開到 Infinity」是同一個病。
 *
 * ⚠️ 純函式：只看 window 自己的欄位與兩個常數，沒有牆上時鐘。同一個輸入永遠得到
 *    同一個範圍（F5 之後重算的邊界與重整前一致）。
 *
 * ⚠️ **單一區間看不到鄰居**，所以這一支算不出「放行量有沒有伸進下一個區間」。
 *    要拿去做歸屬判定一律走 photoEditWindowRanges（複數），它會再夾一次下一個
 *    區間的起點。這一支只服務「還開著的那一段」那種天生沒有下一個的情況
 *    （getAsrPhotoEditing）與單元測試。
 *
 * @param {{startSec:number, endSec:number|null, endSource:string|null}} window
 * @returns {{from:number, to:number}}
 */
export function photoEditWindowRange(window) {
  const from = Number((window && window.startSec) || 0)
  const hasEnd = window && window.endSec !== null && window.endSec !== undefined
  const explicitTo = hasEnd
    ? Number(window.endSec) + (window.endSource === 'manual' ? REFINE_COMMIT_LAG_SEC : 0)
    : Infinity
  return { from, to: Math.min(explicitTo, from + PHOTO_EDIT_MAX_OPEN_SEC) }
}

/**
 * 一整組區間的有效範圍——**歸屬判定唯一該用的那一支**。
 *
 * 與逐段呼叫 photoEditWindowRange 的差別只有一件事，但那件事是必要的：
 * **每一段的 `to` 再夾一次下一段的 `startSec`**，區間之間因此永不重疊。
 *
 * 為什麼非夾不可：滑鼠收尾的放行量（REFINE_COMMIT_LAG_SEC）是對「標記落後多少」
 * 的保守估計，而下一個 `photo_select` 走 fast 通道、不落後——它是**直接證據**，
 * 估計值不該蓋過直接證據。不夾的話兩段會重疊 `12 - lag` 秒，落在重疊區的行與
 * 兩段的重疊秒數完全相等，平手規則會把它判給**舊**卡片：新卡片什麼都收不到，
 * 而它的病灶名／大小／位置全寫到上一張上（實測誤寫率 5.53% → 夾完 0.00%）。
 *
 * @param {Array<{startSec:number, endSec:number|null, endSource:string|null}>} windows
 *        依 startSec 排序（photoEditWindows 的輸出天生如此）
 * @returns {Array<{from:number, to:number}>} 與 windows 同序、同長度
 */
export function photoEditWindowRanges(windows) {
  const list = Array.isArray(windows) ? windows : []
  return list.map((window, index) => {
    const range = photoEditWindowRange(window)
    const next = list[index + 1]
    if (!next) return range
    const nextFrom = Number(next.startSec)
    if (!Number.isFinite(nextFrom)) return range
    return { from: range.from, to: Math.min(range.to, nextFrom) }
  })
}

/** 一行 refine 的時間範圍；end 缺失或不合理時退化成「零長度的一點」 */
function lineRange(line) {
  const start = Number(line && line.start)
  if (!Number.isFinite(start)) return null
  const rawEnd = Number(line && line.end)
  return { start, end: Number.isFinite(rawEnd) && rawEnd > start ? rawEnd : start }
}

/**
 * 這一行有沒有落進這個區間（區間重疊判定）。
 *
 * ── 為什麼是「區間重疊」而不是比起點 ──
 *
 * `from` 來自 fast 通道某一輪 hypothesis 的 word start，`line.start` 來自 refine
 * commit 的另一輪重解碼——**兩條各自獨立的時間軸**，同一個字兩邊算出的秒數不
 * 相等。用 `line.start >= from` 這種嚴格比較，實測差 30ms 就整行丟失，而一次
 * refine commit 常常是「photo 3 + 後面那串口述」一整段，等於口述能不能寫進卡片
 * 變成擲硬幣。改成重疊判定之後，「講 photo 3 的那一段」天然橫跨 `from`，
 * 兩軸的對齊誤差就不再有影響。
 *
 * `start >= from` 那一項是舊規則，留著讓「零長度且剛好落在 from」不會反而漏掉。
 */
function lineOverlapsRange(range, { from, to }) {
  return (range.end > from || range.start >= from) && range.start < to
}

/** 這一行與這個區間重疊了幾秒（沒重疊回 0） */
function overlapSeconds(range, { from, to }) {
  return Math.max(0, Math.min(range.end, to) - Math.max(range.start, from))
}

/**
 * 把每一行 refine 分配給**恰好一個**編輯區間，回傳與 windows 同序的 keywords 陣列。
 *
 * ── 為什麼要單一歸屬 ──
 *
 * 光有重疊判定會製造新的誤寫：一句話跨越指令邊界是**常態**（「…adenoma，十毫米…
 * photo three」是同一段 refine），單純逐區間各自收的話，這一段會同時算進前後兩張
 * 卡片，**下一張卡片憑空得到上一張的病灶名**——醫師拍完就沒再口述的話，那筆錯的
 * 值會一路帶進報告，而且畫面上完全看不出來。這正是本次修正要消滅的那類靜默誤寫。
 *
 * 歸屬規則：**重疊最久的那個區間**。
 *   - 「photo 3 + 之後的口述」同一段 → 絕大部分落在新區間 → 歸新的（P0-4 要解的）
 *   - 「口述 + 句尾才講 photo 3」同一段 → 絕大部分落在舊區間 → 歸舊的（不污染新卡片）
 *   - 兩邊一樣久（罕見）→ 取**較早**的那個區間，與「醫師是在哪個編輯狀態下開口的」
 *     這個原始直覺一致；windows 依 at_sec 排序，所以「較早」＝索引較小。
 *
 * ⚠️ 純函式：只吃 lines 與 windows，同一組輸入永遠得到同一組結果（F5 冪等）。
 *
 * ⚠️ **一定要把整組區間一起交進來**，不要為了方便逐段呼叫（`keywordsByWindow(lines,[w])`）。
 *    只有一個區間就沒有競爭對手，單一歸屬退化成「這一段自己收」——跨越指令邊界的
 *    那一行會被前後兩張卡片各收一次（舊病復發），而且 photoEditWindowRanges 也
 *    夾不到下一段的起點。單元測試裡的單段呼叫是刻意的例外。
 *
 * @param {Array<{start:number, end:number, keywords:string[]}>} lines refine 逐字稿
 * @param {Array<{startSec:number, endSec:number|null}>} windows 依時間排序的編輯區間
 * @returns {object[][]} 與 windows 同序、同長度的 committed line buckets
 */
export function linesByWindow(lines, windows) {
  const list = Array.isArray(windows) ? windows : []
  // ⚠️ 一定要走複數版：它會把滑鼠收尾的放行量夾在下一個區間的起點上，
  //    否則兩段重疊、落在重疊區的行會被平手規則判給舊卡片（見該函式）
  const ranges = photoEditWindowRanges(list)
  const buckets = list.map(() => [])

  const sorted = (Array.isArray(lines) ? lines : [])
    .map((line) => ({ line, range: lineRange(line) }))
    .filter((item) => item.range)
    .sort((a, b) => a.range.start - b.range.start)

  for (const { line, range } of sorted) {
    let bestIndex = -1
    let bestOverlap = -1
    for (let i = 0; i < ranges.length; i += 1) {
      if (!lineOverlapsRange(range, ranges[i])) continue
      const overlap = overlapSeconds(range, ranges[i])
      // 嚴格大於 ⇒ 平手時保留先找到的（索引較小＝時間較早的區間）
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestIndex = i
      }
    }
    if (bestIndex < 0) continue
    buckets[bestIndex].push(line)
  }

  return buckets
}

/** 舊 consumer 只需要 keywords；行歸屬仍由上面的唯一實作決定。 */
export function keywordsByWindow(lines, windows) {
  return linesByWindow(lines, windows).map((bucket) => (
    bucket.reduce((keywords, line) => (
      Array.isArray(line && line.keywords) ? keywords.concat(line.keywords) : keywords
    ), [])
  ))
}
